import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleAlert, MessageSquarePlus, CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";
import { useFirma } from "../context/FirmaContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const CAT_LABEL = {
  alvo: "Alvo intestinale", alzate: "Alzate", messa_a_letto: "Messa a letto",
  controllo_terapie: "Controllo terapie", non_mangia: "Non mangia", non_beve: "Non beve",
  docce: "Docce", parametri: "Parametri vitali", infettive: "Malattie infettive",
};

// Hook condiviso: una sola query per pagina, condivisa da badge e da tutti i pulsanti riga.
export function useSegnalazioni(categoria, structureId, data) {
  const enabled = !!structureId && structureId !== "all" && !!categoria;
  const { data: res, refetch } = useQuery({
    enabled,
    queryKey: ["segnalazioni", categoria, structureId, data || "all"],
    queryFn: async () => (await api.get("/segnalazioni", { params: { structure_id: structureId, categoria, ...(data ? { data } : {}) } })).data,
  });
  const byKey = {};
  (res?.segnalazioni || []).forEach((s) => { byKey[`${s.ospite_id}_${s.data}`] = s; });
  return { byKey, aperte: res?.aperte || 0, refetch };
}

// Piccolo box riassuntivo con il numero di segnalazioni aperte.
export function SegnalazioniBadge({ categoria, aperte = 0 }) {
  return (
    <div className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 ${aperte > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-secondary/40 text-muted-foreground"}`} data-testid={`segn-badge-${categoria}`}>
      <CircleAlert size={16} className={aperte > 0 ? "text-amber-600" : ""} />
      <span className="text-sm font-semibold">Segnalazioni</span>
      <span className={`text-sm font-head font-bold tabnum rounded-full px-2 ${aperte > 0 ? "bg-amber-500 text-white" : "bg-secondary text-foreground"}`}>{aperte}</span>
    </div>
  );
}

// Pulsante "!" per riga/cella: apre il dialog con le note (append-only) e permette di aggiungerne una.
export function SegnalazioneButton({ categoria, structureId, data, ospiteId, ospiteNome, targetOperatoreId = "", targetOperatoreNome = "", entry, onChanged, size = 20 }) {
  const { identity } = useFirma();
  const { can, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  const note = entry?.note || [];
  const nOpen = entry && entry.stato !== "chiusa" ? note.length : 0;
  const risolta = entry?.stato === "chiusa";

  const add = async () => {
    const t = testo.trim();
    if (!t) { toast.error("Scrivi la segnalazione"); return; }
    setBusy(true);
    try {
      await api.post("/segnalazioni", {
        structure_id: structureId, categoria, data: data || "", ospite_id: ospiteId, ospite_nome: ospiteNome,
        testo: t, operatore_id: identity?.operatore_id || "", operatore_nome: identity?.operatore_nome || "",
        target_operatore_id: targetOperatoreId || "", target_operatore_nome: targetOperatoreNome || "",
      });
      setTesto(""); toast.success("Segnalazione registrata");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const chiudi = async () => {
    if (!entry?.id) return;
    const closer = identity?.operatore_nome || ((user?.role === "admin" || user?.role === "super_admin") ? "Admin" : "");
    setBusy(true);
    try { await api.post(`/segnalazioni/${entry.id}/chiudi`, { chiuso_da: closer }); toast.success("Segnalazione risolta"); onChanged && onChanged(); setOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const hasOpen = nOpen > 0;
  const noStruct = !structureId || structureId === "all";
  const tid = `segn-btn-${categoria}-${ospiteId}${data ? `-${data}` : ""}`;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={noStruct} data-testid={tid}
        title={noStruct ? "Seleziona prima una struttura" : hasOpen ? `${nOpen} segnalazione/i` : "Segnala qualcosa su questo punto"}
        className={`relative inline-grid place-items-center h-8 w-8 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${hasOpen ? "bg-amber-100 border-amber-400 text-amber-700" : risolta ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-white border-input text-muted-foreground hover:border-amber-400 hover:text-amber-600"}`}>
        {risolta && !hasOpen ? <CheckCircle2 size={size - 2} /> : <span className="font-head font-extrabold text-lg leading-none">!</span>}
        {hasOpen && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 grid place-items-center tabnum">{nOpen}</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`segn-dialog-${categoria}-${ospiteId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CircleAlert size={18} className="text-amber-600" /> Segnalazioni · {CAT_LABEL[categoria] || categoria}</DialogTitle>
            <DialogDescription>Le segnalazioni non si possono modificare: ognuno può aggiungerne una nuova, firmata con nome e ora.</DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <div className="mb-2"><b>{ospiteNome}</b>{data ? <span className="text-muted-foreground"> · {data}</span> : null}{risolta && <span className="ml-2 text-xs font-semibold text-emerald-700">✓ risolta</span>}</div>
            {note.length === 0 ? (
              <p className="text-muted-foreground italic mb-3">Nessuna segnalazione. Scrivine una qui sotto: resterà visibile a tutti e non modificabile.</p>
            ) : (
              <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1">
                {note.map((n, i) => (
                  <div key={i} className="rounded-lg border bg-amber-50/50 border-amber-100 p-2.5" data-testid={`segn-nota-${i}`}>
                    <div className="text-sm">{n.testo}</div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5"><Clock size={11} /> {n.operatore_nome || "—"}{n.ora ? ` · ${n.ora}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
            <Textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={3} placeholder="Scrivi la tua segnalazione (chi la scrive resta registrato)…" data-testid={`segn-input-${categoria}-${ospiteId}`} />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {entry?.id && !risolta && can("correggi_spunte") && (
              <Button variant="outline" onClick={chiudi} disabled={busy} className="sm:mr-auto text-emerald-700 border-emerald-300" data-testid={`segn-risolvi-${ospiteId}`}><CheckCircle2 size={15} className="mr-1" /> Segna risolta</Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button onClick={add} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid={`segn-save-${categoria}-${ospiteId}`}><MessageSquarePlus size={15} className="mr-1" /> Aggiungi segnalazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
